import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import { useSmartSend } from '../../contexts/SmartSendContext';
import formStyles from '../../styles/SmartSendForm.module.css';
import DatePickerModal from './DatePickerModal';
import {
  getTodayInTimezone,
  formatDateForInput,
  formatDateForInputDisplay,
  isSameDay,
  parseDateInput,
  calculateDateRange,
} from '../../utils/deliveryDateUtils';
import { formatMessageWithSentenceBreaks } from '../../utils/textFormatting';
import { isDevelopment, ANALYTICS_SOURCE } from '../../env/config';
import analytics from '../../../../library/segmentAnalytics';

// ============================================
// CONSTANTS
// ============================================
const SEND_NOW_MESSAGE =
  'Most gifts arrive within 5–7 business days of order, please note that delivery times can vary based on destination and carrier delays.';

// Preview message configuration constants
const SHORT_MESSAGE_THRESHOLD = 200; // Character threshold for showing full message without "See More"
const FALLBACK_SPLIT_RATIO = 0.6; // Ratio of message to show in preview when sentence detection fails

/**
 * DeliveryFormStep - Delivery date, time, and instructions collection
 * Implements SmartSend delivery scheduling logic with timezone support
 *
 * Features:
 * - Dynamic title based on order type (delivered vs shipped)
 * - Offset-based date range calculation
 * - Send Now / Schedule Later mode selection
 * - Timezone-aware date/time handling
 * - Conditional time slot rendering
 *
 * @param {Function} onAddressEdit - Handler to allow editing the address
 * @param {Function} onConfirm - Handler for final form submission
 * @param {boolean} isSubmitting - Loading state during submission
 */
const DeliveryFormStep = ({
  onAddressEdit,
  onConfirm,
  isSubmitting = false,
}) => {
  // ============================================
  // CONTEXT STATE
  // ============================================
  const {
    address,
    zoningData,
    order,
    orderId,
    deliveryDate,
    deliveryTime,
    deliveryInstructions,
    setDeliveryDate,
    setDeliveryTime,
    setDeliveryInstructions,
  } = useSmartSend();

  // ============================================
  // LOCAL STATE
  // ============================================
  const [deliveryMode, setDeliveryMode] = useState('schedule'); // 'now' | 'schedule'
  const [isMoreInfoOpen, setIsMoreInfoOpen] = useState(false);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

  // Track last auto-set date to prevent unnecessary updates
  const lastAutoSetDateRef = useRef(null);

  // ============================================
  // EXTRACT ZONING CONFIG
  // ============================================
  const {
    offset: dataOffset = 0,
    deliveryConfig = {},
    latestDeliveryOffset = 7,
  } = zoningData || {};

  const {
    default: isDefault = true,
    displaySendNow = false,
    displaySendLater = true,
    displayDeliveryTime: deliveryConfigDisplayTime = true,
    hasPriority = false,
    offset: deliveryConfigOffset = 0,
    sendNowText,
    sendLaterText,
  } = deliveryConfig;

  // Check order.displayDeliveryTime first (authoritative for order type like Goody),
  // then fall back to deliveryConfig.displayDeliveryTime, then default to true
  const displayDeliveryTime =
    order?.displayDeliveryTime ?? deliveryConfigDisplayTime ?? true;

  // ============================================
  // CALCULATE ACTIVE OFFSET
  // ============================================
  const activeOffset = hasPriority ? deliveryConfigOffset : dataOffset;

  // ============================================
  // TIMEZONE
  // ============================================
  const timezone = address?.timezone || 'America/New_York';

  // ============================================
  // DATE RANGE CALCULATION
  // ============================================
  // Follows the same pattern as original SmartSend:
  // Backend calculates offsets from SKU dates (if applicable) and returns them
  // Frontend calculates dates using: today + offset and today + latestDeliveryOffset
  const dateRange = useMemo(() => {
    // Only calculate date range if we have valid data
    if (!address || !zoningData) {
      return {
        earliestDate: null,
        latestDate: null,
        earliestDateStr: '',
        latestDateStr: '',
      };
    }

    // Calculate date range using offsets (which already account for SKU date restrictions)
    return calculateDateRange(activeOffset, latestDeliveryOffset, timezone);
  }, [activeOffset, latestDeliveryOffset, timezone, address, zoningData]);

  const { earliestDateStr, latestDateStr } = dateRange;

  // ============================================
  // DETERMINE ORDER TYPE
  // ============================================
  // Check multiple possible property names for order type
  // Non-gesture types include: 'goody', 'physical', 'cardly', etc.
  const orderType = useMemo(() => {
    // Try multiple possible property names
    const type =
      order?.order_type ||
      order?.orderType ||
      order?.type ||
      order?.skuType ||
      order?.productType ||
      'gesture';

    // Normalize to lowercase for comparison
    const normalizedType = String(type).toLowerCase();

    // Debug logging in development
    if (isDevelopment() && order) {
      console.log('[DeliveryFormStep] Order type detection:', {
        order_type: order.order_type,
        orderType: order.orderType,
        type: order.type,
        skuType: order.skuType,
        productType: order.productType,
        detectedType: normalizedType,
        fullOrder: order,
      });
    }

    // Return 'gesture' if type is 'gesture', otherwise return the detected type
    // This allows for 'goody', 'physical', 'cardly', etc. to be treated as non-gesture
    return normalizedType === 'gesture' ? 'gesture' : normalizedType;
  }, [order]);

  // ============================================
  // DETERMINE DISPLAY MODE
  // ============================================
  const shouldShowToggle = !isDefault && displaySendNow && displaySendLater;
  const shouldShowSendNowOnly =
    !isDefault && displaySendNow && !displaySendLater;
  const shouldAutoSetSendNow = shouldShowSendNowOnly;
  const shouldShowSchedulerOnly =
    !isDefault && !displaySendNow && displaySendLater;

  // Hide Shipping Date section when all options are false
  // This happens for non-Gesture SKUs where Send Now & Send Later are hidden
  const shouldHideShippingDateSection =
    !isDefault && !displaySendNow && !displaySendLater;

  // ============================================
  // DETERMINE TITLE
  // ============================================
  const title = useMemo(() => {
    // When date/time inputs are hidden, show review title with line break
    if (shouldHideShippingDateSection) {
      return (
        <>
          Review your Gesture
          <br />
          delivery details
        </>
      );
    }

    // Default titles based on order type
    if (orderType !== 'gesture') {
      return 'When should we send your Gesture?';
    }

    // Split into two equal lines to prevent orphan "delivered?"
    return (
      <>
        When would you like your
        <br />
        Gesture delivered?
      </>
    );
  }, [orderType, shouldHideShippingDateSection]);

  // ============================================
  // DETERMINE DATE LABEL
  // ============================================
  const dateLabel = useMemo(() => {
    // Use "Shipping Date" for non-gesture order types (Goody, cardly, etc.)
    return orderType === 'gesture' ? 'Delivery Date' : 'Shipping Date';
  }, [orderType]);

  // ============================================
  // DETERMINE MESSAGES FOR SEND NOW / SEND LATER
  // ============================================
  // Use backend messages from deliveryConfig if available, otherwise fall back to hardcoded defaults
  // Pattern matches displayDeliveryTime fallback logic above
  // Edge cases handled:
  // - null/undefined: falls back to hardcoded message
  // - empty string: falls back to hardcoded message (consistent with || operator behavior)
  // - non-string values: falls back to hardcoded message (defensive programming)
  const sendNowMessage = useMemo(() => {
    // Ensure sendNowText is a non-empty string before using it
    const message = typeof sendNowText === 'string' && sendNowText.trim()
      ? sendNowText
      : SEND_NOW_MESSAGE;
    // Format message with sentence breaks (each sentence on new line)
    return formatMessageWithSentenceBreaks(message);
  }, [sendNowText]);

  const sendLaterMessage = useMemo(() => {
    // Use backend message if available and valid, otherwise use hardcoded default
    // Ensure sendLaterText is a non-empty string before using it
    let message;
    if (typeof sendLaterText === 'string' && sendLaterText.trim()) {
      message = sendLaterText;
    } else {
      // Fallback to hardcoded message with dynamic latest date info
      message =
        'Your gift will be shipped on the date you select and will typically arrive within 5–7 business days. Please note that delivery times can vary based on destination and carrier delays.';
      if (latestDateStr) {
        message += ` This product can be scheduled through ${latestDateStr}.`;
      }
    }
    // Format message with sentence breaks (each sentence on new line)
    return formatMessageWithSentenceBreaks(message);
  }, [sendLaterText, latestDateStr]);

  // Get the message to display based on current mode
  const currentMessage = useMemo(() => {
    if (deliveryMode === 'now') {
      return sendNowMessage;
    }
    return sendLaterMessage;
  }, [deliveryMode, sendNowMessage, sendLaterMessage]);

  // ============================================
  // PARSE MESSAGE FOR PREVIEW
  // ============================================
  // Extract first two lines for preview/expand functionality
  // Messages are already formatted with sentence breaks, so we work with lines
  const messagePreview = useMemo(() => {
    // Determine which message to use - when shouldShowSendNowOnly is true, always use sendNowMessage
    // This avoids unnecessary dependency on currentMessage which depends on deliveryMode
    const message = shouldShowSendNowOnly ? sendNowMessage : currentMessage;

    if (!message || typeof message !== 'string') {
      return { previewLines: '', remainingText: '', hasMore: false, previewLinesArray: [] };
    }

    // Messages are already formatted with sentence breaks, so split by explicit line breaks
    const lines = message.split('\n').filter(line => line.trim());

    if (lines.length > 2) {
      // Message has more than 2 lines
      const previewLines = lines.slice(0, 2).join('\n');
      const remainingText = lines.slice(2).join('\n');
      return { previewLines, remainingText, hasMore: true, previewLinesArray: lines.slice(0, 2) };
    }

    // Message has 2 or fewer lines, but might still be long
    const fullText = lines.join('\n');

    // If message is short, show it all
    if (fullText.length <= SHORT_MESSAGE_THRESHOLD) {
      return { previewLines: fullText, remainingText: '', hasMore: false, previewLinesArray: [fullText] };
    }

    // For longer messages without multiple line breaks, try to split by sentences
    // This handles edge cases where formatMessageWithSentenceBreaks didn't find sentence endings
    const sentencePattern = /([^.!?]+[.!?])\s*/g;
    const sentences = [];
    let match;
    let lastIndex = 0;

    while ((match = sentencePattern.exec(fullText)) !== null && sentences.length < 2) {
      sentences.push(match[1].trim());
      lastIndex = match.index + match[0].length;
    }

    if (sentences.length >= 2) {
      const previewLines = sentences.join(' ');
      const remainingText = fullText.substring(lastIndex).trim();
      return { previewLines, remainingText, hasMore: remainingText.length > 0, previewLinesArray: sentences };
    }

    // If we can't find 2 sentences, split at threshold or 60% of message length
    const splitPoint = Math.min(SHORT_MESSAGE_THRESHOLD, Math.floor(fullText.length * FALLBACK_SPLIT_RATIO));
    const previewLines = fullText.substring(0, splitPoint).trim();
    const remainingText = fullText.substring(splitPoint).trim();
    // For character-based split, treat as single line
    return { previewLines, remainingText, hasMore: remainingText.length > 0, previewLinesArray: [previewLines] };
  }, [shouldShowSendNowOnly, sendNowMessage, currentMessage]);

  // ============================================
  // INITIALIZE ON MOUNT - Clear any invalid delivery data
  // ============================================
  useEffect(() => {
    // Clear any invalid date format on mount
    if (deliveryDate && !deliveryDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
      setDeliveryDate('');
      setDeliveryTime('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ============================================
  // NOTE: Date auto-setting behavior
  // ============================================
  // In most cases, users must explicitly select a date to prevent accidental submissions
  // with incorrect dates. The date picker shows "Select Date" placeholder until selection.
  // Exception: When all options are false (default=false, displaySendNow=false, displaySendLater=false),
  // the date is automatically set to 'today' as if the user selected "Send Now".

  // ============================================
  // HANDLE SEND NOW
  // ============================================
  const handleSendNow = useCallback(() => {
    setDeliveryMode('now');

    const today = getTodayInTimezone(timezone);
    const todayDateStr = formatDateForInput(today);

    // Set delivery date to today (regardless of offset - backend will handle validation)
    setDeliveryDate(todayDateStr);

    // Set delivery time to "Within 2 hours" (only for same-day delivery orders)
    // For shipped orders (like Goody), time is not applicable
    const timeSlot = displayDeliveryTime ? 'within-2-hours' : '';
    if (displayDeliveryTime) {
      setDeliveryTime('within-2-hours');
    } else {
      setDeliveryTime('');
    }

    // Track date/time selection when user clicks "Send Now"
    if (orderId && todayDateStr && typeof todayDateStr === 'string') {
      analytics.track('Date/Time Selected', {
        source: ANALYTICS_SOURCE,
        date: todayDateStr,
        time: timeSlot || null,
        orderId,
      });
    }
  }, [timezone, displayDeliveryTime, setDeliveryMode, setDeliveryDate, setDeliveryTime, orderId]);

  // ============================================
  // REVALIDATE SELECTED DATE WHEN ZONING DATA CHANGES
  // ============================================
  useEffect(() => {
    // Skip revalidation if section is hidden (date is auto-set to today)
    if (shouldHideShippingDateSection) {
      return;
    }

    // If user has selected a date and zoning data changes (e.g., address change),
    // validate that the selected date is still within the valid range
    if (
      deliveryDate &&
      earliestDateStr &&
      latestDateStr &&
      address?.timezone &&
      zoningData
    ) {
      const selectedDate = parseDateInput(deliveryDate);
      const minDate = parseDateInput(earliestDateStr);
      const maxDate = parseDateInput(latestDateStr);

      // If selected date is outside the new valid range, clear it completely
      // User must explicitly select a new date (prevents accidentally using old date)
      if (selectedDate < minDate || selectedDate > maxDate) {
        setDeliveryDate(''); // Clear instead of resetting to earliestDateStr
        setDeliveryTime(''); // Clear time when date is cleared
        toast.info(
          'Please select a new delivery date based on the updated address restrictions.'
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [earliestDateStr, latestDateStr, address?.timezone, zoningData, shouldHideShippingDateSection]);

  // ============================================
  // INITIALIZE SEND NOW MODE IF REQUIRED
  // ============================================
  useEffect(() => {
    // Only auto-set Send Now if we have valid address and zoning data
    if (shouldAutoSetSendNow && address?.timezone && zoningData) {
      handleSendNow();
    }
  }, [shouldAutoSetSendNow, address?.timezone, zoningData, handleSendNow]);

  // ============================================
  // AUTO-SET DATE TO TODAY WHEN SECTION IS HIDDEN
  // ============================================
  // When all options are false (default=false, displaySendNow=false, displaySendLater=false),
  // hide the section but auto-set date to 'today' as if user selected send now
  useEffect(() => {
    if (
      shouldHideShippingDateSection &&
      address?.timezone &&
      zoningData
    ) {
      // Always ensure date is set to today when section is hidden
      // This handles both initial load and cases where date might get cleared
      const today = getTodayInTimezone(timezone);
      const todayDateStr = formatDateForInput(today);

      // Only update if date is not already set to today and we haven't already set this exact date
      // This prevents unnecessary re-renders and potential infinite loops
      if (deliveryDate !== todayDateStr && lastAutoSetDateRef.current !== todayDateStr) {
        setDeliveryDate(todayDateStr);
        lastAutoSetDateRef.current = todayDateStr;

        // Set delivery time to "Within 2 hours" (only for same-day delivery orders)
        // For shipped orders (like Goody), time is not applicable
        if (displayDeliveryTime) {
          setDeliveryTime('within-2-hours');
        } else {
          setDeliveryTime('');
        }
      }
    } else {
      // Reset ref when section is no longer hidden
      lastAutoSetDateRef.current = null;
    }
  }, [
    shouldHideShippingDateSection,
    address?.timezone,
    timezone,
    zoningData,
    deliveryDate,
    displayDeliveryTime,
  ]);

  // ============================================
  // HANDLE SCHEDULE LATER
  // ============================================
  const handleScheduleLater = () => {
    setDeliveryMode('schedule');

    // Clear selections to force user to pick
    setDeliveryDate('');
    setDeliveryTime('');
  };

  // ============================================
  // HANDLE MODE TOGGLE
  // ============================================
  const handleModeToggle = (mode) => {
    if (mode === 'now') {
      handleSendNow();
    } else {
      handleScheduleLater();
    }
    // Close more info when switching modes
    setIsMoreInfoOpen(false);
  };

  // ============================================
  // HANDLE DATE CHANGE
  // ============================================
  const handleDateChange = (dateStr) => {
    // Validate date is within range
    // The dateRange is calculated from offsets which already account for SKU date restrictions
    if (dateStr && earliestDateStr && latestDateStr) {
      const selectedDate = parseDateInput(dateStr);
      const minDate = parseDateInput(earliestDateStr);
      const maxDate = parseDateInput(latestDateStr);

      if (selectedDate < minDate || selectedDate > maxDate) {
        toast.error(
          `Please select a date between ${earliestDateStr} and ${latestDateStr}`
        );
        return;
      }
    }

    setDeliveryDate(dateStr);

    // Clear time when date changes (force re-selection)
    // Note: Tracking happens when user clicks "Save" in the modal, not on individual selections
    setDeliveryTime('');
  };

  // ============================================
  // CALCULATE AVAILABLE TIME SLOTS
  // ============================================
  const availableTimeSlots = useMemo(() => {
    if (!deliveryDate) return [];

    const selectedDate = parseDateInput(deliveryDate);
    const today = getTodayInTimezone(timezone);
    const isToday = isSameDay(selectedDate, today);

    // If today and offset = 0, only "Within 2 hours"
    if (isToday && activeOffset === 0) {
      return ['within-2-hours'];
    }

    // If future date, show standard time slots
    if (!isToday) {
      return ['10am-2pm', '2pm-8pm'];
    }

    // Default: no time slots (shouldn't reach here normally)
    return [];
  }, [deliveryDate, timezone, activeOffset]);

  // ============================================
  // FORMAT TIME SLOT FOR DISPLAY
  // ============================================
  const formatTimeSlot = (timeSlot) => {
    if (!timeSlot) return '';

    const timeMap = {
      'within-2-hours': 'Within 2 hours',
      'before-noon': 'Before Noon',
      '10am-2pm': '10am–2pm',
      '2pm-8pm': '2pm–8pm',
    };

    return timeMap[timeSlot] || timeSlot;
  };

  // ============================================
  // FORMAT ADDRESS FOR DISPLAY
  // ============================================
  const formatAddress = (addr) => {
    if (!addr || !addr.address1) return 'No address selected';

    const parts = [
      addr.address1,
      addr.address2,
      addr.locality,
      `${addr.state} ${addr.postcode}`,
      addr.country,
    ].filter(Boolean);

    return parts.join(', ');
  };

  // ============================================
  // HANDLE ADDRESS EDIT
  // ============================================
  const handleEditAddress = () => {
    if (onAddressEdit) {
      onAddressEdit();
    }
  };

  // ============================================
  // FORM VALIDATION
  // ============================================
  const isFormValid = useMemo(() => {
    if (!deliveryDate) return false;

    // If displayDeliveryTime is false, date is enough
    if (!displayDeliveryTime) return true;

    // Otherwise, need both date and time
    return Boolean(deliveryTime);
  }, [deliveryDate, deliveryTime, displayDeliveryTime]);

  // ============================================
  // DETERMINE IF DATE SECTION SHOULD BE VISIBLE
  // ============================================
  const shouldShowDateSection = useMemo(() => {
    // Hide if all options are false
    if (shouldHideShippingDateSection) return false;

    // Show if default mode (and not send now only)
    if (isDefault && !shouldShowSendNowOnly) return true;

    // Show if scheduler only mode
    if (shouldShowSchedulerOnly) return true;

    // Show if user selected schedule mode
    if (deliveryMode === 'schedule') return true;

    return false;
  }, [
    shouldHideShippingDateSection,
    isDefault,
    shouldShowSendNowOnly,
    shouldShowSchedulerOnly,
    deliveryMode,
  ]);

  // ============================================
  // RENDER
  // ============================================

  // Don't render until we have valid address and zoning data
  if (!address || !zoningData) {
    return (
      <div className={formStyles.card}>
        <div className={formStyles.addressFormWrapper}>
          <p className={formStyles.loadingMessage}>
            Loading delivery options...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={formStyles.card}
      role="form"
      aria-label="Delivery details form"
    >
      {/* Title */}
      <h2 className={formStyles.sectionTitle} id="delivery-form-title">
        {title}
      </h2>

      <div
        className={formStyles.addressFormWrapper}
        role="group"
        aria-labelledby="delivery-form-title"
      >
        {/* Address Display - Read-only with edit option */}
        <div className={formStyles.fieldGroup}>
          <label className={formStyles.label} id="address-label">
            Delivery Address
          </label>
          <div
            className={formStyles.addressDisplay}
            role="group"
            aria-labelledby="address-label"
          >
            <span
              className={formStyles.addressDisplayText}
              id="current-address"
            >
              {formatAddress(address)}
            </span>
            <button
              type="button"
              className={formStyles.editButton}
              onClick={handleEditAddress}
              aria-label="Edit delivery address"
              aria-describedby="current-address"
            >
              Edit
            </button>
          </div>
        </div>

        {/* Mode Selection Toggle (if applicable) */}
        {shouldShowToggle && (
          <div className={formStyles.fieldGroup}>
            <div
              className={formStyles.modeToggle}
              role="radiogroup"
              aria-label="Delivery timing options"
            >
              <button
                type="button"
                className={`${formStyles.modeButton} ${
                  deliveryMode === 'now' ? formStyles.modeButtonActive : ''
                }`}
                onClick={() => handleModeToggle('now')}
                role="radio"
                aria-checked={deliveryMode === 'now'}
                aria-label="Send Now"
              >
                Send Now
              </button>
              <button
                type="button"
                className={`${formStyles.modeButton} ${
                  deliveryMode === 'schedule' ? formStyles.modeButtonActive : ''
                }`}
                onClick={() => handleModeToggle('schedule')}
                role="radio"
                aria-checked={deliveryMode === 'schedule'}
                aria-label="Send Later"
              >
                Send Later
              </button>
            </div>
          </div>
        )}

        {/* Send Now only state: show a locked button for clarity */}
        {shouldShowSendNowOnly && (
          <div className={formStyles.fieldGroup}>
            <div
              className={formStyles.modeToggle}
              role="radiogroup"
              aria-label="Delivery timing option"
            >
              <button
                type="button"
                className={`${formStyles.modeButton} ${formStyles.modeButtonActive}`}
                disabled
                role="radio"
                aria-checked="true"
              >
                Send Now
              </button>
            </div>
          </div>
        )}

        {/* Show scheduler when mode is 'schedule' or always show if default */}
        {/* Hide entire Shipping Date section when all options are false (default=false, displaySendNow=false, displaySendLater=false) */}
        {shouldShowDateSection && (
          <>
            {/* Delivery Date & Time - Combined Input */}
            <div className={formStyles.fieldGroup}>
              <label className={formStyles.label} htmlFor="date">
                {displayDeliveryTime ? `${dateLabel} & Time` : dateLabel}
              </label>
              <div className={formStyles.dateInputWrapper}>
                <button
                  type="button"
                  id="date"
                  className={`${formStyles.input} ${formStyles.dateInput} ${
                    formStyles.dateInputButton
                  } ${deliveryDate ? formStyles.hasValue : ''}`}
                  onClick={() => setIsDatePickerOpen(true)}
                  aria-label={`Select ${
                    displayDeliveryTime
                      ? `${dateLabel.toLowerCase()} and time`
                      : dateLabel.toLowerCase()
                  }`}
                  aria-describedby="date-help"
                >
                  {deliveryDate ? (
                    <>
                      {formatDateForInputDisplay(
                        parseDateInput(deliveryDate)
                      )}
                      {displayDeliveryTime && deliveryTime && (
                        <span className={formStyles.dateTimeSeparator}>
                          {' '}
                          •{' '}
                        </span>
                      )}
                      {displayDeliveryTime && deliveryTime && (
                        <span>{formatTimeSlot(deliveryTime)}</span>
                      )}
                    </>
                  ) : null}
                </button>
                {!deliveryDate && (
                  <span
                    className={formStyles.datePlaceholder}
                    aria-hidden="true"
                  >
                    {displayDeliveryTime
                      ? `Select ${dateLabel.toLowerCase()} and time`
                      : `Select ${dateLabel.toLowerCase()}`}
                  </span>
                )}
              </div>
              <span id="date-help" className="sr-only">
                {displayDeliveryTime
                  ? `Select a date between ${earliestDateStr} and ${latestDateStr}, and a delivery time window`
                  : `Select a date between ${earliestDateStr} and ${latestDateStr}`}
              </span>
            </div>

            {/* More Info section - for non-gesture SKUs, show below shipping date */}
            {orderType !== 'gesture' &&
              (shouldShowToggle || shouldShowSendNowOnly) && (
                <div className={formStyles.moreInfoContainer}>
                  {/* Message content - shows first two lines, or full message when expanded */}
                  {messagePreview.hasMore && !isMoreInfoOpen && messagePreview.previewLinesArray.length > 0 ? (
                    <div className={formStyles.modeMessagePreviewWrapper}>
                      <div className={formStyles.modeMessagePreviewFade}>
                        {messagePreview.previewLinesArray.map((line, index) => (
                          <p
                            key={`preview-line-${index}-${line.substring(0, 20)}`}
                            className={formStyles.modeMessagePreview}
                          >
                            {line}
                          </p>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div
                      className={formStyles.modeMessagePreviewWrapper}
                      id={messagePreview.hasMore ? 'more-info-content' : undefined}
                    >
                      {(shouldShowSendNowOnly ? sendNowMessage : currentMessage)
                        .split('\n')
                        .filter(line => line.trim())
                        .map((line, index) => (
                          <p
                            key={`message-line-${index}-${line.substring(0, 20)}`}
                            className={formStyles.modeMessagePreview}
                          >
                            {line}
                          </p>
                        ))}
                    </div>
                  )}

                  {/* See More/See Less button */}
                  {messagePreview.hasMore && (
                    <button
                      type="button"
                      className={formStyles.seeMoreButton}
                      onClick={() => setIsMoreInfoOpen(!isMoreInfoOpen)}
                      aria-expanded={isMoreInfoOpen}
                      aria-controls="more-info-content"
                    >
                      {isMoreInfoOpen ? 'See Less' : 'See More'}
                    </button>
                  )}
                </div>
              )}
          </>
        )}

        {/* Delivery Instructions - Only show for gesture orders */}
        {orderType === 'gesture' && (
          <div>
            <label className={formStyles.label} htmlFor="instructions">
              Delivery Instructions (Optional)
            </label>
            <textarea
              id="instructions"
              className={formStyles.textArea}
              placeholder="Type here..."
              maxLength={300}
              value={deliveryInstructions}
              onChange={(e) => setDeliveryInstructions(e.target.value)}
              aria-label="Enter any special delivery instructions"
              aria-describedby="instructions-help"
            />
            <span id="instructions-help" className="sr-only">
              Optional field for special instructions to the delivery person,
              maximum 300 characters
            </span>
          </div>
        )}

        {/* More Info section - for gesture SKUs, show below delivery instructions */}
        {/* Only show when there are delivery mode options (toggle or send now only) */}
        {orderType === 'gesture' &&
          (shouldShowToggle || shouldShowSendNowOnly) && (
            <div className={formStyles.moreInfoContainer}>
              {/* Message content - shows first two lines, or full message when expanded */}
              {messagePreview.hasMore && !isMoreInfoOpen && messagePreview.previewLinesArray.length > 0 ? (
                <div className={formStyles.modeMessagePreviewWrapper}>
                  <div className={formStyles.modeMessagePreviewFade}>
                    {messagePreview.previewLinesArray.map((line, index) => (
                      <p
                        key={`preview-line-${index}-${line.substring(0, 20)}`}
                        className={formStyles.modeMessagePreview}
                      >
                        {line}
                      </p>
                    ))}
                  </div>
                </div>
              ) : (
                <div
                  className={formStyles.modeMessagePreviewWrapper}
                  id={messagePreview.hasMore ? 'more-info-content' : undefined}
                >
                  {(shouldShowSendNowOnly ? sendNowMessage : currentMessage)
                    .split('\n')
                    .filter(line => line.trim())
                    .map((line, index) => (
                      <p
                        key={`message-line-${index}-${line.substring(0, 20)}`}
                        className={formStyles.modeMessagePreview}
                      >
                        {line}
                      </p>
                    ))}
                </div>
              )}

              {/* See More/See Less button */}
              {messagePreview.hasMore && (
                <button
                  type="button"
                  className={formStyles.seeMoreButton}
                  onClick={() => setIsMoreInfoOpen(!isMoreInfoOpen)}
                  aria-expanded={isMoreInfoOpen}
                  aria-controls="more-info-content"
                >
                  {isMoreInfoOpen ? 'See Less' : 'See More'}
                </button>
              )}
            </div>
          )}
      </div>

      <button
        className={formStyles.button}
        onClick={onConfirm}
        disabled={!isFormValid || isSubmitting}
        aria-label={
          isSubmitting
            ? 'Submitting delivery details'
            : 'Confirm and submit delivery details'
        }
        aria-busy={isSubmitting}
        aria-describedby="age-verification-notice"
      >
        {isSubmitting ? 'Submitting...' : 'Confirm'}
      </button>

      <p className={formStyles.note} id="age-verification-notice" role="note">
        We'll need to verify your age (21+) if your gift is an alcoholic gift. A
        photo ID must be provided at delivery.
      </p>

      {/* Date Picker Modal - Only render when section is visible */}
      {!shouldHideShippingDateSection &&
        earliestDateStr &&
        latestDateStr && (
          <DatePickerModal
          isOpen={isDatePickerOpen}
          onClose={() => setIsDatePickerOpen(false)}
          onDateSelect={(dateStr) => {
            handleDateChange(dateStr);
            // Date is only saved when user clicks "Save" in the modal
            // Modal closes automatically after Save is clicked
          }}
          selectedDate={deliveryDate}
          earliestDateStr={earliestDateStr}
          latestDateStr={latestDateStr}
          timezone={timezone}
          displayDeliveryTime={displayDeliveryTime}
          availableTimeSlots={availableTimeSlots}
          selectedTime={deliveryTime}
          onTimeSelect={(timeSlot) => {
            setDeliveryTime(timeSlot);
            // Note: Tracking happens when user clicks "Save" in the modal, not on individual selections
            // This prevents duplicate events when both date and time are selected simultaneously
          }}
          onSave={({ date, time }) => {
            // Track date/time selection when user clicks "Save" in the modal
            // This ensures a single event is tracked even when both date and time are selected
            if (orderId && date && typeof date === 'string') {
              analytics.track('Date/Time Selected', {
                source: ANALYTICS_SOURCE,
                date,
                time: time || null,
                orderId,
              });
            }
          }}
          activeOffset={activeOffset}
          orderType={orderType}
        />
      )}
    </div>
  );
};

export default DeliveryFormStep;
